import {
  styled,
  TextField,
  FormControl,
  MenuProps,
  Checkbox,
  RadioProps,
  Radio,
  InputLabel,
} from "@mui/material";
import { CustomTextFieldProps } from "./form-config.model";
import { DatePicker, DesktopDatePicker } from "@mui/x-date-pickers";

// Styles for CustomTextField
export const CustomStyledTextField = styled(TextField)<CustomTextFieldProps>(
  ({ theme, error, success }) => ({
    "& .MuiInputBase-root": { borderRadius: 4 },
    "& .MuiFormLabel-root": {
      position: "static",
      transform: "none",
      marginBottom: theme.spacing(0.5),
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.body2.fontSize,
      lineHeight: theme.typography.body2.lineHeight,
    },
    "& .MuiOutlinedInput-root": {
      ...(error && {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.error.main,
        },
      }),
      ...(success && {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.success.main,
        },
      }),
    },
    "& .MuiFormHelperText-root": {
      ...(error && { color: theme.palette.error.main }),
      ...(success && { color: theme.palette.success.main }),
    },
  })
);
export const CustomStyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: theme.typography.body2.lineHeight,
  position: "static", // Keeps the label in the same position
  transform: "none", // Prevents transformations
  marginBottom: theme.spacing(0.5), // Consistent spacing
  width: "100%", // Ensures full width
  whiteSpace: "normal", // Allows text wrapping
  wordBreak: "break-word", // Prevents overflow with long text
  display: "block", // Makes the label behave like a block element
  "& .MuiInputLabel-asterisk": {
    color: theme.palette.error.main, // Custom asterisk color
  },
  "&.MuiFormLabel-root": {
    position: "static", // Prevent movement in the root state
    transform: "none", // Prevent label shift
  },
  "&.MuiFormLabel-root.Mui-focused": {
    position: "static", // Ensure no movement on focus
    transform: "none", // Prevent transform on focus

  },
}));

// Styles for FormControl in CustomSelect
export const StyledFormControl = styled(FormControl)<{
  size?: "medium" | "small";
}>(({ theme, size }) => ({
  marginBottom: theme.spacing(2),
  minWidth: "100%",
  "& .MuiInputLabel-root": {
    position: "static",
    transform: "none",
    marginBottom: theme.spacing(0.5),
    fontFamily: theme.typography.fontFamily,
    fontSize: size === 'small' ? theme.typography.body2.fontSize : theme.typography.body2.fontSize,
    lineHeight: size === 'small' ? theme.typography.body2.lineHeight : theme.typography.body2.lineHeight,

  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: 4,
  },
  "& .Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.error.main,
  },
  "& .Mui-error .MuiFormHelperText-root": {
    color: theme.palette.error.main,
  },
}));

export const StyledMenuProps: Partial<MenuProps> = {
  PaperProps: {
    style: {
      maxHeight: 200,
      marginTop: 8,
      borderRadius: 8,
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
      overflowY: "auto",
    },
  },
};

export const CustomStyledRadio = styled(
  (
    props: RadioProps & { sizeVariant?: "medium" | "small"; disabled?: boolean }
  ) => <Radio disableRipple {...props} />
)(({ theme, sizeVariant = "small", disabled }) => ({
  // Base styling for unselected state
  color: disabled ? theme.palette.grey[500] : theme.palette.text.primary,

  // Adjust the size of the radio button based on the sizeVariant prop
  "& .MuiSvgIcon-root": {
    width: sizeVariant === "medium" ? "16px" : "14px", // Medium and small sizes
    height: sizeVariant === "medium" ? "16px" : "14px",
    borderRadius: "50%", // Ensure circular shape
    boxSizing: "border-box",
    // border: `2px solid ${disabled ? 'rgba(0, 0, 0, 0.38)' : '#000000'}`, // Consistent black border
    backgroundColor: "transparent", // Transparent background by default
    opacity: disabled ? 0.5 : 1, // Reduced opacity for inactive state
  },

  // Styling when checked
  "&.Mui-checked .MuiSvgIcon-root": {
    backgroundColor: disabled ? "rgba(229, 183, 104, 0.5)" : "#E5B768", // Yellow background or faded for disabled
    border: `1px solid ${disabled ? "rgba(0, 0, 0, 0.38)" : "#000000"}`, // Consistent black border
  },

  // Black inner dot for checked state
  "&.Mui-checked": {
    color: disabled ? "rgba(0, 0, 0, 0.38)" : "#000000", // Gray dot for inactive
  },

  // Handle unselected inactive state
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.38)", // Gray border for disabled state
  },
}));
// Styled Checkbox
export const CustomStyledCheckbox = styled(Checkbox)(({ theme }) => ({
  // Base styling
  color: theme.palette.grey[400], // Default color for unselected state
  "& .MuiSvgIcon-root": {
    fontSize: 20, // Default size, adjust as needed
  },
  "&.Mui-disabled": {
    color: theme.palette.grey[300], // Inactive state color
  },

  // Checked state
  "&.Mui-checked": {
    color: theme.palette.primary.main, // Main color for selected state
  },

  // Indeterminate state
  "& .MuiCheckbox-indeterminate": {
    color: theme.palette.warning.main, // Color for indeterminate state
  },

  // Inactive checked state
  "&.Mui-disabled.Mui-checked": {
    color: theme.palette.grey[500], // Inactive color for checked state
  },

  // Inactive indeterminate state
  "&.Mui-disabled .MuiCheckbox-indeterminate": {
    color: theme.palette.grey[500], // Inactive color for indeterminate state
  },

  // Different sizes
  "&.small": {
    "& .MuiSvgIcon-root": {
      fontSize: 16, // Smaller font size for small variant
    },
  },
}));
// Styled DatePicker
export const CustomStyledDatePicker = styled(DesktopDatePicker)(
  ({ theme }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 4, // Matching TextField radius
      '& fieldset': {
        borderColor: theme.palette.grey[400],
      },
      // '&:hover fieldset': {
      //   borderColor: theme.palette.primary.main,
      // },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.fontFamily,
    position: 'static',
    transform: 'none',
    marginBottom: theme.spacing(0.5),
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary,
    },
  })
);
// Styled DateRangePicker
export const CustomStyledDateRangePicker = styled(DatePicker)(
  ({ theme }) => ({
    width: "50%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 4, // Matching TextField radius
      '& fieldset': {
        borderColor: theme.palette.grey[400],
      },
      // '&:hover fieldset': {
      //   borderColor: theme.palette.primary.main,
      // },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.fontFamily,
    position: 'static',
    transform: 'none',
    marginBottom: theme.spacing(0.5),
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary,
    },
  })
);
